<template>
  <div class="configuration-tab">
    <app-loading :loading="isLoading" is-background />

    <ValidationObserver v-if="!isLoading" v-slot="v">
      <b-form id="edit-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
        <div class="form-container">
          <!-- Basic Details -->
          <basic-details-section
            :name="awardForm.name"
            :selected-division-ids="selectedDivisionIds"
            :division-options="divisionOptions"
            @update:name="awardForm.name = $event"
            @update:selectedDivisionIds="onDivisionsChange"
          >
            <template #after-divisions>
              <hr />

              <div class="company-values-container">
                <div class="label-row">
                  <div class="label">
                    Add your company values (optional)
                    <small>
                      Tag relevant values to help employees understand what behaviours this award recognises. You can
                      use our suggestions or customise them to match your organisation's values.
                    </small>
                  </div>
                  <b-form-checkbox v-model="companyValuesEnabled" switch />
                </div>
                <div v-if="companyValuesEnabled">
                  <create-company-values-modal v-model="companyValues" />
                </div>
              </div>
            </template>
          </basic-details-section>

          <!-- Appearance -->
          <image-upload-section
            :award-name="awardForm.name"
            :banner-file="bannerFile"
            :banner-preview-url="bannerPreviewUrl"
            :badge-file="badgeFile"
            :badge-preview-url="badgePreviewUrl"
            :allowed-image-types="allowedImageTypes"
            :max-size-bytes="MAX_BANNER_SIZE_BYTES"
            @update:bannerFile="onBannerFileUpdate"
            @update:bannerPreviewUrl="onBannerPreviewUrlUpdate"
            @update:badgeFile="onBadgeFileUpdate"
            @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
            @gallerySelect:banner="onBannerGallerySelect"
            @gallerySelect:badge="onBadgeGallerySelect"
          />

          <!-- Award Details -->
          <div>
            <h4>Award details</h4>
            <sme-card large>
              <div class="card-container">
                <div class="label-row">
                  <div class="label">
                    Enable badges (optional)
                    <small>Turn awards into collectible badges that appear on recipients' home page.</small>
                  </div>
                  <b-form-checkbox v-model="badgesEnabled" switch />
                </div>

                <monetary-reward-section
                  :enabled="monetaryRewardEnabled"
                  :currency-symbol="currencySymbol"
                  :amounts="awardValueMinor"
                  :default-amount-minor="defaultAwardValueMinor"
                  :amount-error="awardValueError"
                  :redemption-options="selectedRedemptionOptions"
                  :redemption-error="redemptionSelectionError"
                  :expiration-months="expirationMonths"
                  @update:enabled="monetaryRewardEnabled = $event"
                  @update:amounts="awardValueMinor = $event"
                  @update:defaultAmountMinor="onDefaultAwardValueMinorChange"
                  @update:redemptionOptions="selectedRedemptionOptions = $event"
                  @update:expirationMonths="expirationMonths = $event"
                >
                  <template #after-amounts>
                    <budget-settings-section
                      v-if="monetaryRewardEnabled"
                      :currency-symbol="currencySymbol"
                      :amount="rewardBudgetAmount"
                      :cadence="rewardBudgetCadence"
                      :year-start-month="budgetYearStartMonth"
                      :year-start-day="budgetYearStartDay"
                      :year-start-day-options="budgetYearStartDayOptions"
                      :year-start-disabled="budgetYearStartDisabled"
                      :year-start-month-rules="budgetYearStartMonthRules"
                      :year-start-day-rules="budgetYearStartDayRules"
                      @update:amount="rewardBudgetAmount = $event"
                      @update:cadence="rewardBudgetCadence = $event"
                      @update:yearStartMonth="budgetYearStartMonth = $event"
                      @update:yearStartDay="budgetYearStartDay = $event"
                    />
                  </template>
                </monetary-reward-section>
              </div>
            </sme-card>
          </div>

          <!-- Nominations & Approvals -->
          <div>
            <h4>Nominations & Approvals</h4>
            <sme-card large>
              <div class="label-permissions-and-approvals">Permissions and approvals</div>

              <div class="nomination-type-container">
                <app-input
                  v-model="closedNominationsEnabled"
                  :validate-immediate="false"
                  name="Nomination type"
                  type="select"
                  class="mb-0"
                  :options="nominationOptions"
                >
                  <template #label>
                    <div class="label">Who can nominate?</div>
                  </template>
                </app-input>

                <multi-select
                  v-if="closedNominationsEnabled"
                  id="nominators-select"
                  :value="selectedNominatorIds"
                  :options="employeeOptions"
                  placeholder="Select who can nominate for this award"
                  :searchable="true"
                  search-placeholder="Search by name or email"
                  :disabled="employeesLoading || divisionEmployeePairsLoading"
                  :select-all="false"
                  select-all-text="No nominators available"
                  class="mb-0"
                  @input="onNominatorsChange"
                />
                <small v-if="nominatorSelectionError" class="text-danger">{{ nominatorSelectionError }}</small>
              </div>

              <div class="approver-container">
                <multi-select
                  id="approvers-select"
                  :value="selectedApproverIds"
                  :options="employeeOptions"
                  placeholder="Select who can approve this award"
                  :searchable="true"
                  search-placeholder="Search by name or email"
                  :disabled="employeesLoading || divisionEmployeePairsLoading"
                  :select-all="false"
                  select-all-text="No approvers available"
                  class="mb-0"
                  @input="onApproversChange"
                >
                  <template #label>
                    <div class="label">Who can approve?</div>
                  </template>
                </multi-select>
                <small v-if="approverSelectionError" class="text-danger">{{ approverSelectionError }}</small>
              </div>
            </sme-card>
          </div>

          <div>
            <h4>Award status</h4>
            <sme-card large>
              <div class="label-row">
                <div class="label">
                  Award active
                  <small
                    >When disabled, this award will not be visible to employees and no new nominations can be
                    made.</small
                  >
                </div>
                <b-form-checkbox v-model="isActive" switch />
              </div>
            </sme-card>
          </div>
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="resetForm"> Reset </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || !hasChanges">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Saving...' : 'Save changes' }}
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import { RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import { useDivisionEmployeeFiltering } from '@/pages/recognition/composables/useDivisionEmployeeFiltering';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';
import BudgetSettingsSection from '@/pages/recognition/components/BudgetSettingsSection.vue';

const props = defineProps({
  program: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

// Use shared composable
const {
  currencySymbol,
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  monetaryRewardEnabled,
  awardValueMinor,
  awardValueError,
  defaultAwardValueMinor,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  rewardBudgetAmount,
  rewardBudgetCadence,
  budgetYearStartMonth,
  budgetYearStartDay,
  budgetYearStartDayOptions,
  budgetYearStartDisabled,
  budgetYearStartMonthRules,
  budgetYearStartDayRules,
  bannerFile,
  bannerPreviewUrl,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBannerFileUpdate,
  onBannerPreviewUrlUpdate,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  onBannerGallerySelect,
  onBadgeGallerySelect,
  bannerS3Key,
  badgeS3Key,
  saving,
  errorMessage,
  onDefaultAwardValueMinorChange,
  loadDivisions,
  initBudgetYearStart,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  buildBaseProperties,
  getImageData,
  getBudgetMinor,
  getGrantExpiryInterval,
  persistBudgetYearStart,
  validateRedemptionOptions,
  validateAwardValues,
  validateBudgetCadence,
} = useAwardForm();

// Division-based employee filtering
const {
  employeesLoading,
  divisionEmployeePairsLoading,
  eligibleEmployeeIds,
  employeeOptions,
  refreshEligibleEmployeeIds,
  loadEmployeesAndDivisionPairs,
} = useDivisionEmployeeFiltering(selectedDivisionIds);

// Program-specific state
const badgesEnabled = ref(true);
const companyValuesEnabled = ref(false);
const companyValues = ref([]);
const selectedApproverIds = ref([]);
const selectedNominatorIds = ref([]);
const closedNominationsEnabled = ref(false);
const nominatorSelectionError = ref('');
const approverSelectionError = ref('');
const isActive = ref(true);
const isLoading = ref(true);

// Track original values for change detection
const originalValues = ref({});

const { doToast, TOAST_VARIANT } = useToast();

const nominationOptions = [
  { text: 'Closed nominations (only selected users can nominate)', value: true },
  { text: 'Open nominations (anyone can nominate)', value: false },
];

// Detect if form has changes
const hasChanges = computed(() => {
  const current = getCurrentFormState();
  return JSON.stringify(current) !== JSON.stringify(originalValues.value);
});

/**
 * Parse ISO 8601 duration to months.
 */
const parseGrantExpiryInterval = interval => {
  if (!interval) {
    return '';
  }
  const str = String(interval);
  const monthMatch = str.match(/^P(\d+)M$/);
  if (monthMatch) {
    return monthMatch[1];
  }
  const dayMatch = str.match(/^P(\d+)D$/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    const months = Math.round(days / 30);
    return months > 0 ? String(months) : '';
  }
  return '';
};

/**
 * Get current form state for comparison
 */
const getCurrentFormState = () => ({
  name: awardForm.value.name,
  selectedDivisionIds: [...(selectedDivisionIds.value || [])].sort(),
  companyValuesEnabled: companyValuesEnabled.value,
  companyValues: [...(companyValues.value || [])].sort(),
  badgesEnabled: badgesEnabled.value,
  monetaryRewardEnabled: monetaryRewardEnabled.value,
  awardValueMinor: awardValueMinor.value,
  defaultAwardValueMinor: defaultAwardValueMinor.value,
  selectedRedemptionOptions: [...(selectedRedemptionOptions.value || [])].sort(),
  expirationMonths: expirationMonths.value,
  rewardBudgetAmount: rewardBudgetAmount.value,
  rewardBudgetCadence: rewardBudgetCadence.value,
  budgetYearStartMonth: budgetYearStartMonth.value,
  budgetYearStartDay: budgetYearStartDay.value,
  closedNominationsEnabled: closedNominationsEnabled.value,
  selectedNominatorIds: [...(selectedNominatorIds.value || [])].sort(),
  selectedApproverIds: [...(selectedApproverIds.value || [])].sort(),
  isActive: isActive.value,
  bannerFileName: bannerFile.value?.name ?? null,
  badgeFileName: badgeFile.value?.name ?? null,
  bannerS3Key: bannerS3Key.value,
  badgeS3Key: badgeS3Key.value,
});

/**
 * Populate form from program data
 */
const populateFromProgram = program => {
  if (!program) {
    return;
  }

  // Basic details
  awardForm.value.name = program.program_name || '';
  isActive.value = program.is_active !== false;
  badgesEnabled.value = program.show_badge !== false;

  // Divisions
  const divisions = program.enabled_divisions || [];
  selectedDivisionIds.value = divisions.length > 0 ? divisions : [];

  // Company values
  const programCompanyValues = program.properties?.company_values || [];
  if (programCompanyValues.length > 0) {
    companyValuesEnabled.value = true;
    companyValues.value = programCompanyValues.map(v => v.name || v);
  } else {
    companyValuesEnabled.value = false;
    companyValues.value = [];
  }

  // Images - set preview URLs and S3 keys from existing images
  if (program.banner_presigned_url) {
    bannerPreviewUrl.value = program.banner_presigned_url;
  } else {
    bannerPreviewUrl.value = '';
  }
  bannerS3Key.value = program.banner_s3_key || '';
  if (program.badge_presigned_url) {
    badgePreviewUrl.value = program.badge_presigned_url;
  } else {
    badgePreviewUrl.value = '';
  }
  badgeS3Key.value = program.badge_s3_key || '';
  // Clear any previously selected files
  onBannerFileUpdate(null);
  onBadgeFileUpdate(null);

  // Monetary reward
  const hasMonetaryReward = program.properties?.award_value_minor != null;
  monetaryRewardEnabled.value = hasMonetaryReward;

  if (hasMonetaryReward) {
    awardValueMinor.value = program.properties.award_value_minor;
    defaultAwardValueMinor.value = program.properties.default_award_value_minor;

    // Redemption options
    const validGrantTypes = program.properties?.valid_grant_types || ['VOUCHER'];
    selectedRedemptionOptions.value = validGrantTypes;

    // Expiration
    expirationMonths.value = parseGrantExpiryInterval(program.grant_expiry_interval);

    // Budget
    if (program.budget_minor) {
      rewardBudgetAmount.value = (program.budget_minor / 100).toFixed(2);
    } else {
      rewardBudgetAmount.value = '';
    }
    if (program.properties?.budget_period) {
      rewardBudgetCadence.value = program.properties.budget_period;
    } else {
      rewardBudgetCadence.value = undefined;
    }
  } else {
    // Clear monetary reward fields when not enabled
    awardValueMinor.value = undefined;
    defaultAwardValueMinor.value = undefined;
    selectedRedemptionOptions.value = [];
    expirationMonths.value = '';
    rewardBudgetAmount.value = '';
    rewardBudgetCadence.value = undefined;
  }

  // Nominators (independent of monetary rewards)
  const nominators = program.properties?.nominators || [];
  if (nominators.length > 0) {
    closedNominationsEnabled.value = true;
    selectedNominatorIds.value = nominators;
  } else {
    closedNominationsEnabled.value = false;
    selectedNominatorIds.value = [];
  }

  // Approvers (independent of monetary rewards)
  const approverGroups = program.properties?.approvers || [];
  if (approverGroups.length > 0) {
    const allApproverIds = approverGroups.flatMap(g => g.approvers || []);
    selectedApproverIds.value = allApproverIds;
  } else {
    selectedApproverIds.value = [];
  }

  // Store original values for change detection
  originalValues.value = getCurrentFormState();
};

/**
 * Reset form to original program values
 */
const resetForm = () => {
  populateFromProgram(props.program);
  // Clear any newly selected image files
  onBannerFileUpdate(null);
  onBadgeFileUpdate(null);
  errorMessage.value = '';
  approverSelectionError.value = '';
  nominatorSelectionError.value = '';
  awardValueError.value = '';
  redemptionSelectionError.value = '';
};

const buildProperties = (approverIds, redemptionMethods, nominators) => {
  const props = buildBaseProperties(redemptionMethods);

  // Set approvers to null when empty to explicitly clear them
  props.approvers = (approverIds || []).length
    ? [
        {
          name: 'Default',
          approvers: (approverIds || []).map(String),
        },
      ]
    : null;

  // Set nominators to null when empty to explicitly clear them
  props.nominators = Array.isArray(nominators) && nominators.length > 0 ? (nominators || []).map(String) : null;

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      props.company_values = values.map(name => ({ name }));
    }
  }

  return props;
};

const buildPayload = properties => ({
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.ALL,
  show_badge: badgesEnabled.value,
  enabled_divisions: enabledDivisions.value,
  budget_minor: getBudgetMinor(),
  grant_expiry_interval: getGrantExpiryInterval(),
  is_active: isActive.value,
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const approvers = selectedApproverIds.value || [];
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];
  const nominators = closedNominationsEnabled.value ? selectedNominatorIds.value || [] : [];

  try {
    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require at least one approver for program awards
    if (!approvers || approvers.length === 0) {
      approverSelectionError.value = 'Please select at least one approver.';
      saving.value = false;
      return;
    }
    approverSelectionError.value = '';

    // Require at least one nominator when closed nominations are enabled
    if (closedNominationsEnabled.value && (!nominators || nominators.length === 0)) {
      nominatorSelectionError.value = 'Please select at least one nominator.';
      saving.value = false;
      return;
    }
    nominatorSelectionError.value = '';

    if (!validateAwardValues()) {
      saving.value = false;
      return;
    }

    if (!validateBudgetCadence()) {
      saving.value = false;
      return;
    }

    const approverIds = (approvers || []).map(String);
    const properties = buildProperties(approverIds, redemptionMethods, nominators);
    const payload = buildPayload(properties);
    const { files, s3Keys } = getImageData();

    const budgetResult = await persistBudgetYearStart();
    if (!budgetResult.success) {
      errorMessage.value = budgetResult.error;
      saving.value = false;
      return;
    }

    await ApiClient.updateRecognitionProgram(props.program.recognition_award_program_id, payload, files, s3Keys);

    doToast('Success', 'Award updated successfully', TOAST_VARIANT.SUCCESS);
    originalValues.value = getCurrentFormState();
    emit('updated');
  } catch (e) {
    errorMessage.value = e?.variables?.message || e?.message || 'Failed to update award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = ids => {
  selectedDivisionIds.value = ids;
  refreshEligibleEmployeeIds();
  const allowed = eligibleEmployeeIds.value || new Set();
  selectedApproverIds.value = (selectedApproverIds.value || []).filter(id => allowed.has(id));
  selectedNominatorIds.value = (selectedNominatorIds.value || []).filter(id => allowed.has(id));
};

const onApproversChange = ids => {
  selectedApproverIds.value = ids;
};

const onNominatorsChange = ids => {
  selectedNominatorIds.value = ids;
};

watch(selectedApproverIds, newVal => {
  if (approverSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    approverSelectionError.value = '';
  }
});

watch(selectedNominatorIds, newVal => {
  if (nominatorSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    nominatorSelectionError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
  }
});

watch(closedNominationsEnabled, enabled => {
  if (!enabled) {
    selectedNominatorIds.value = [];
    nominatorSelectionError.value = '';
  }
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([loadDivisions(), ...loadEmployeesAndDivisionPairs()]);
    initBudgetYearStart();
    setupCommonWatchers();
    populateFromProgram(props.program);
    refreshEligibleEmployeeIds();
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => props.program,
  newProgram => {
    if (newProgram) {
      populateFromProgram(newProgram);
      refreshEligibleEmployeeIds();
    }
  },
);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.configuration-tab {
  position: relative;
  min-height: 200px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}
</style>
